import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, X, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { useT } from '@/lib/i18n';
import { callChatAssistant } from '@/lib/functions';
import { renderMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { NEUNEU_ACTIONS } from './neuNeuActions';
import { NEUNEU_TRANSITION_MS } from './neuNeuConstants';
import type { ChatMessage } from './types';

interface NeuNeuPanelProps {
  open: boolean;
  onClose: () => void;
  /** Opens beside the sidebar on desktop, or above the floating FAB on mobile. */
  placement?: 'sidebar' | 'fab';
}

function newId() {
  return crypto.randomUUID();
}

export function NeuNeuPanel({ open, onClose, placement = 'fab' }: NeuNeuPanelProps) {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    { id: newId(), role: 'model', text: t('neuneu.greeting') },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;

    const userMsg: ChatMessage = { id: newId(), role: 'user', text: trimmed };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    setBusy(true);

    try {
      const payload = history.map(m => ({ role: m.role, text: m.text }));
      const { data } = await callChatAssistant({ messages: payload, locale });
      setMessages(prev => [...prev, { id: newId(), role: 'model', text: data.reply }]);
    } catch (e) {
      const rateLimited = (e as { code?: string }).code === 'functions/resource-exhausted';
      toast.error(rateLimited ? t('neuneu.rate_limited') : (e as Error).message || t('neuneu.error'));
      setMessages(prev => prev.slice(0, -1));
      setInput(trimmed);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send(input);
  };

  return (
    <div
      role="dialog"
      aria-label={t('neuneu.title')}
      aria-hidden={!open}
      className={cn(
        'fixed z-70 flex w-[min(100vw-2rem,28rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl transition-all ease-out dark:border-border/50 dark:bg-card',
        placement === 'sidebar'
          ? 'bottom-6 left-68 max-h-[min(70vh,560px)]'
          : 'bottom-36 right-4 sm:right-6',
        open ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0',
      )}
      style={{ transitionDuration: `${NEUNEU_TRANSITION_MS}ms` }}
    >
      <header className="flex items-center gap-3 border-b border-slate-200 bg-brand-50/60 px-4 py-3 dark:border-border/50 dark:bg-brand-900/20">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 font-display text-sm text-ink-900 dark:text-white">
            <Sparkles className="h-3.5 w-3.5 text-brand-500" />
            {t('neuneu.title')}
          </div>
          <p className="truncate text-xs text-ink-500">{t('neuneu.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1.5 text-ink-400 transition hover:bg-slate-100 hover:text-ink-700 dark:hover:bg-secondary dark:hover:text-foreground"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="border-b border-slate-100 px-3 py-2 dark:border-border/30">
        <div className="flex flex-wrap gap-1.5">
          {NEUNEU_ACTIONS.map(action => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                type="button"
                disabled={busy}
                onClick={() => action.run({ navigate, t, closePanel: onClose })}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[0.68rem] font-medium text-ink-700 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 disabled:opacity-50 dark:border-border/50 dark:bg-secondary/50 dark:text-foreground/80 dark:hover:border-primary/40 dark:hover:bg-primary/10 dark:hover:text-primary"
              >
                <Icon className="h-3 w-3" />
                {t(action.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div ref={listRef} className="flex max-h-80 min-h-52 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
        {messages.map(msg => (
          <div
            key={msg.id}
            className={cn(
              'max-w-[92%] rounded-xl px-3 py-2 text-sm',
              msg.role === 'user'
                ? 'ml-auto bg-brand-500 text-white'
                : 'mr-auto border border-slate-200 bg-slate-50 text-ink-800 dark:border-border/50 dark:bg-secondary/40 dark:text-foreground',
            )}
          >
            {msg.role === 'model' ? (
              <div
                className="markdown-body text-sm [&_p:last-child]:mb-0 [&_p]:text-inherit"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
              />
            ) : (
              <p className="whitespace-pre-wrap">{msg.text}</p>
            )}
          </div>
        ))}
        {busy && (
          <div className="mr-auto flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-border/50 dark:bg-secondary/40">
            <Loader label={t('neuneu.thinking')} />
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="border-t border-slate-200 p-3 dark:border-border/50">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            rows={2}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(input);
              }
            }}
            placeholder={t('neuneu.input.ph')}
            disabled={busy}
            maxLength={2000}
            className="input min-h-11 flex-1 resize-none py-2"
          />
          <Button type="submit" size="icon" disabled={busy || !input.trim()} aria-label={t('neuneu.send')}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}
